import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { db, storage } from '../../firebase';
import { doc, getDoc, addDoc, updateDoc, collection, serverTimestamp, query, orderBy, getDocs } from 'firebase/firestore';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import FadeIn from '../../components/FadeIn';
import { ArrowLeft, Save } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { triggerAutoUpdate } from '../../utils/sitemapService';
import { callAI, searchImage, generateImage } from '../../utils/aiService';

const BlogEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation(); // Add useLocation hook
    const isEditing = !!id;

    const [title, setTitle] = useState('');
    const [slug, setSlug] = useState('');
    const [content, setContent] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [category, setCategory] = useState('');
    const [image, setImage] = useState(null);
    const [imageUrl, setImageUrl] = useState('');
    const [previewUrl, setPreviewUrl] = useState('');
    const [published, setPublished] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const slugify = (text) => {
        return text
            .toString()
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')     // Replace spaces with -
            .replace(/[^\w-]+/g, '')   // Remove all non-word chars
            .replace(/--+/g, '-');     // Replace multiple - with single -
    };

    // Quill modules configuration
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'align': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'image'],
            ['clean']
        ],
    };

    const [authors, setAuthors] = useState([]);
    const [selectedAuthor, setSelectedAuthor] = useState('');
    const [isDrafting, setIsDrafting] = useState(false);
    const [draftProgress, setDraftProgress] = useState(0);
    const [draftingStage, setDraftingStage] = useState('');

    const generateTemplateFallback = useCallback((item) => {
        const { provider } = item;
        const currentYear = new Date().getFullYear();

        // Refined fallback that is slightly more varied
        const seedValue = (item.title?.length || 0) + (item.summary?.length || 0);
        const benchmarks = [
            { label: 'Market Velocity', value: (8 + (seedValue % 7)).toFixed(1) + '%' },
            { label: 'Strategic ROI', value: (2 + (seedValue % 3)).toFixed(1) + 'x' },
            { label: 'Efficiency Gain', value: '+' + (12 + (seedValue % 15)).toFixed(0) + '%' }
        ];

        return `
            <section class="mb-16">
                <h2 class="text-3xl font-bold text-[#20222D] mb-8 font-gilroy">Strategic Analysis: ${item.title}</h2>
                <p class="mb-6 text-lg leading-relaxed">${item.summary}</p>
                
                ${item.aiResearch ? `
                <div class="mb-8 p-6 bg-[#A2CB52]/5 border-l-4 border-[#A2CB52] rounded-r-xl">
                    <h4 class="text-xs font-bold text-[#A2CB52] uppercase tracking-widest mb-3">${provider ? provider.toUpperCase() : 'AI'} Intelligence Update (${currentYear})</h4>
                    <p class="text-gray-700 italic leading-relaxed text-sm">"${item.aiResearch}"</p>
                </div>
                ` : ''}

                <div class="grid grid-cols-1 md:grid-cols-3 gap-6 my-10">
                    ${benchmarks.map(b => `
                        <div class="p-8 bg-gray-50 rounded-2xl border border-gray-100 shadow-sm text-center">
                            <span class="block text-3xl font-bold text-[#20222D] mb-2 font-gilroy">${b.value}</span>
                            <span class="text-xs text-gray-500 uppercase tracking-[0.2em] font-bold font-gilroy">${b.label}</span>
                        </div>
                    `).join('')}
                </div>

                <p class="leading-relaxed">This snapshot representing "${item.summary}" illustrates a broader shift in the global BPO landscape. Decision makers must look toward integrated partners who can offer more than just labor arbitrage.</p>
            </section>
        `;
    }, []);

    const performFullAiGeneration = useCallback(async (item, aiCreds) => {
        const { aiResearch, draftType, title, summary, url } = item;

        try {
            const directive = `
                ### SEO + SGE + EEAT Optimized Publishing Framework (2026)
                1. SEARCH INTENT: Align with "${draftType || 'Informational'}". Include keywords naturally (e.g., Global talent strategy, Offshore workforce optimization).
                2. DATA AUTHORITY: Reference at least 2–4 institutions (McKinsey, Gartner, Deloitte, IBPAP, PSA, Bureau of Labor Statistics, etc.).
                3. GLOBAL CONTEXT: Frame the analysis for audiences in US, AU, UK, CA, NZ, SG.
                4. EEAT COMPLIANCE: Use business professional tone, not academic. Provide balanced analysis. Reading level: Executive decision-makers.
                
                ### MANDATORY ARTICLE STRUCTURE:
                - INTRODUCTION (150–200 words): Clearly define the topic, mention geography, explain why it matters now. Include primary keyword.
                - DIRECT ANSWER PARAGRAPH (40–60 words): Mandatory for SGE/AI extraction. A concise executive summary of the primary topic, placed within the Introduction.
                - SECTION 1 – WHAT HAPPENED (Data Summary): Neutral, factual summary of industry reports and labor stats.
                - SECTION 2 – STRATEGIC COMMENTARY (Expert Layer): The "Kinetic Expert Layer". Interpretation of data, cost implications, risk assessment, and market comparison.
                - SECTION 3 – EXECUTIVE IMPLICATIONS: Guide decisions. Address hybrid AI+offshore strategy, cost pressures, and automation impact.
                - FAQ SECTION: Minimum 4 questions (Examples: AI impact, growth outlook, wage inflation). Each answer: 60–120 words.
                
                ### FORMATTING RULES (STRICT):
                - OUTPUT RAW HTML ONLY. No markdown.
                - Use <h2> for major sections and <h3> for sub-points. 
                - Use <ul>/<li> for evidence lists and <p> for deep analysis.
                - Paragraphs: 2–4 lines max for readability. Bold key insights.
                - AUTHOR ATTRIBUTION: Include a placeholder for "Last Updated" date and "Editorial Team".
            `;

            const fullPrompt = `
                ### EXECUTIVE CONTENT MISSION
                Target Topic: ${title}
                Base Intelligence: ${summary}
                ${item.batchItems ? `SOURCE SIGNALS:\n${item.batchItems.map((bi) => `- ${bi.title}: ${bi.summary}`).join('\n')}` : ''}
                Research Intelligence: ${aiResearch || 'N/A'}
                Primary Source: ${url}

                ### TASK: Write a 1500-2000 word High-Authority Market Intelligence Briefing following the SEO + SGE + EEAT Optimized Publishing Framework (2026).
                ${directive}

                ### FINAL CHECKS:
                - MINIMUM LENGTH: 1500 words. Expand deeply on implications and commentary.
                - INTERNAL LINKS: Suggest 3 placeholders for [Related Internal Link: Service Area].
                - EXTERNAL LINKS: Reference outbound authority links to government/research sites.
                - LANGUAGE: Sophisticated Business English. No cliches like "path forward".
            `;

            console.log("DEBUG: Starting Full AI Generation. Target: 1500+ words. Provider:", aiCreds.openai_key ? 'OpenAI' : 'Gemini');

            const content = await callAI('full_article', fullPrompt, aiCreds);

            if (!content || content.length < 1000) {
                console.warn("DEBUG: AI returned short content despite request:", content?.length);
            } else {
                console.log("DEBUG: AI successfully returned long content:", content.length, "chars");
            }

            return content;
        } catch (err) {
            console.error("CRITICAL ERROR in performFullAiGeneration:", err);
            throw err; // Let the caller handle it and display a proper UI error
        }
    }, []);



    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch Authors
                const authorsQuery = query(collection(db, 'authors'), orderBy('name', 'asc'));
                const authorsSnap = await getDocs(authorsQuery);
                const authorsData = authorsSnap.docs.map(doc => ({ id: doc.id, name: doc.data().name }));
                setAuthors(authorsData);

                // Fetch Post if editing
                if (isEditing) {
                    const docRef = doc(db, 'posts', id);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        setTitle(data.title);
                        setSlug(data.slug || '');
                        setContent(data.content);
                        setExcerpt(data.excerpt || '');
                        setCategory(data.category || '');
                        setSelectedAuthor(data.authorId || '');
                        setImageUrl(data.imageUrl || '');
                        setPublished(data.published || false);
                    } else {
                        setError('Post not found');
                    }
                } else if (location.state?.newsItem) {
                    // AUTO-DRAFTING FLOW
                    const item = location.state.newsItem;
                    setIsDrafting(true);

                    // Clean Title (remove type prefix if it was added)
                    setTitle(item.title);
                    setSlug(slugify(item.title));
                    setExcerpt(item.summary);

                    // Smart Categorization
                    const topics = (item.topics || []).map(t => t.toLowerCase());
                    const fullText = (item.title + ' ' + item.summary).toLowerCase();

                    let bestCategory = 'Outsourcing'; // Default
                    if (topics.some(t => t.includes('ai') || t.includes('automation')) || fullText.includes('ai ') || fullText.includes('automation')) {
                        bestCategory = 'AI & Automation';
                    } else if (topics.some(t => t.includes('sales') || t.includes('growth')) || fullText.includes('sales') || fullText.includes('revenue')) {
                        bestCategory = 'Sales Strategy';
                    } else if (topics.some(t => t.includes('remote') || t.includes('work from home')) || fullText.includes('remote work') || fullText.includes('distributed team')) {
                        bestCategory = 'Remote Work';
                    } else if (topics.some(t => t.includes('leadership') || t.includes('strategic')) || fullText.includes('executive') || fullText.includes('decision making')) {
                        bestCategory = 'Leadership';
                    } else if (topics.some(t => t.includes('technology') || t.includes('infrastructure')) || fullText.includes('digital transformation')) {
                        bestCategory = 'Technology';
                    }
                    setCategory(bestCategory);

                    // PREMIUM LANDSCAPE STOCK IMAGES (1200x630 Optimized)
                    const stockImages = {
                        'AI & Automation': 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=1200&h=630&crop=edges',
                        'Sales Strategy': 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=1200&h=630&crop=center',
                        'Remote Work': 'https://images.unsplash.com/photo-1593642532400-2682810df593?auto=format&fit=crop&q=80&w=1200&h=630&crop=center',
                        'Leadership': 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&q=80&w=1200&h=630&crop=center',
                        'Technology': 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80&w=1200&h=630&crop=center',
                        'Outsourcing': 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&q=80&w=1200&h=630&crop=center',
                        'Default': 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=1200&h=630&crop=center'
                    };

                    const initialImage = item.imageUrl || stockImages[bestCategory] || stockImages['Default'];
                    setImageUrl(initialImage);

                    // Simulate progressive drafting for UX
                    let progress = 0;
                    const interval = setInterval(async () => {
                        progress += 5;
                        setDraftProgress(progress);

                        if (progress === 30) setDraftingStage('Optimizing Media Assets...');
                        if (progress === 40) {
                            // Phase 2: Media Enhancement
                            try {
                                const aiSettingsDoc = await getDoc(doc(db, 'admin_settings', 'ai_credentials'));
                                const aiSettings = aiSettingsDoc.exists() ? aiSettingsDoc.data() : {};

                                // Use a local variable to check current image state if needed, 
                                // but here we just want to replace if default or unsplash
                                if (initialImage.includes('unsplash.com/photo') && item.useAI) {
                                    // Try Unsplash Search first
                                    if (aiSettings.unsplash_key) {
                                        const searchQuery = `${bestCategory} business corporate ${item.title.split(' ').slice(0, 3).join(' ')}`;
                                        const newImg = await searchImage(searchQuery, aiSettings.unsplash_key);
                                        if (newImg) setImageUrl(newImg);
                                    }

                                    // Fallback to DALL-E if enabled and still default or no Unsplash
                                    if (aiSettings.use_dalle && aiSettings.openai_key) {
                                        const imgUrl = await generateImage(`A professional, high-end corporate 3D render or professional office photography representing: ${item.title}. Cinematic lighting, 8k, photorealistic.`, aiSettings.openai_key);
                                        if (imgUrl) setImageUrl(imgUrl);
                                    }
                                }
                            } catch (e) { console.error("Media error:", e); }
                        }

                        if (progress === 70) setDraftingStage('Synthesizing 1500+ Word Executive Analysis...');

                        if (progress >= 100) {
                            clearInterval(interval);

                            // Phase 3: Final Content Synthesis
                            try {
                                const aiSettingsDoc = await getDoc(doc(db, 'admin_settings', 'ai_credentials'));
                                const aiSettings = aiSettingsDoc.exists() ? aiSettingsDoc.data() : {};

                                if (item.useAI && (aiSettings.openai_key || aiSettings.gemini_key || aiSettings.grok_key || aiSettings.perplexity_key)) {
                                    try {
                                        const highQualityContent = await performFullAiGeneration(item, aiSettings);
                                        setContent(highQualityContent);

                                        // If the content is just a template (check if it contains the fallback markers)
                                        if (highQualityContent.includes('Strategic Analysis:')) {
                                            setError('AI Quota Limit reached. A high-quality standard template has been applied to proceed.');
                                        }
                                    } catch (aiErr) {
                                        console.error("DEBUG: AI Generation failed:", aiErr);
                                        setContent(''); // Don't use fallback if AI fails, let user read error

                                        let userFriendlyError = 'AI Generation Failed: ' + aiErr.message;
                                        if (aiErr.message.toLowerCase().includes('quota') || aiErr.message.toLowerCase().includes('limit') || aiErr.message.toLowerCase().includes('exceeded')) {
                                            userFriendlyError += ' — RECOMMENDATION: Your AI API key has likely run out of credits. Please check your billing dashboard (OpenAI/Gemini/Grok) and add funds or pay for AI tokens to resume automatic article generation.';
                                        } else if (aiErr.message.toLowerCase().includes('key') || aiErr.message.toLowerCase().includes('unauthorized') || aiErr.message.toLowerCase().includes('permission')) {
                                            userFriendlyError += ' — RECOMMENDATION: Your AI API key is invalid or lacks permissions. Please check your key in Admin Settings > AI Credentials.';
                                        }

                                        setError(userFriendlyError);
                                    }
                                } else {
                                    setContent(generateTemplateFallback(item));
                                    if (item.useAI) {
                                        setError('No AI credentials found. Proceeding with standard expert template.');
                                    }
                                }
                            } catch (error) {
                                console.error("DEBUG: Drafting finalization error:", error);
                                setContent(generateTemplateFallback(item));
                            }
                            setIsDrafting(false);
                        }
                    }, 150);
                } else if (location.state) {
                    // Fallback for simple title/content passing
                    if (location.state.draftTitle) setTitle(location.state.draftTitle);
                    if (location.state.draftContent) setContent(location.state.draftContent);
                }
            } catch (err) {
                setError('Error loading data: ' + err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, isEditing, location.state, performFullAiGeneration, generateTemplateFallback]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleImageUpload = async (file) => {
        if (!file) return null;
        const storageRef = ref(storage, `blog/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            let url = imageUrl;
            if (image) {
                url = await handleImageUpload(image);
            }

            const postData = {
                title,
                slug: slug || slugify(title),
                content,
                excerpt,
                category,
                authorId: selectedAuthor,
                authorName: authors.find(a => a.id === selectedAuthor)?.name || '',
                imageUrl: url, // Use the new URL if uploaded, else keep old
                published,
                updatedAt: serverTimestamp()
            };

            if (isEditing) {
                await updateDoc(doc(db, 'posts', id), postData);
            } else {
                postData.createdAt = serverTimestamp();
                await addDoc(collection(db, 'posts'), postData);
            }

            // Trigger Sitemap Update
            await triggerAutoUpdate();

            navigate('/admin/blog');
        } catch (err) {
            console.error(err);
            setError('Error saving post. ' + (err.code === 'permission-denied' ? 'Check Firestore Rules.' : err.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" style={{ background: 'var(--bg)' }}>
            <div className="max-w-4xl mx-auto">
                <FadeIn>
                    <div className="flex items-center mb-8 space-x-4">
                        <Link to="/admin/blog" style={{ color: 'var(--muted)' }} className="hover:text-white transition-colors">
                            <ArrowLeft size={24} />
                        </Link>
                        <h1 className="text-3xl font-bold text-white">
                            {isEditing ? 'Edit Post' : 'Create New Post'}
                        </h1>
                    </div>

                    <div className="rounded-xl shadow-2xl p-8 relative overflow-hidden border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                        {isDrafting && (
                            <div className="absolute inset-0 z-50 bg-[#090e18]/95 flex flex-col items-center justify-center p-12 text-center text-white backdrop-blur-md animate-in fade-in duration-500">
                                <div className="p-4 bg-[#A2CB52]/20 rounded-full mb-6 animate-pulse">
                                    <Save size={40} className="text-[#A2CB52]" />
                                </div>
                                <h2 className="text-2xl font-bold mb-4">
                                    {location.state?.newsItem?.aiResearch
                                        ? `Synthesizing ${location.state.newsItem.provider?.toUpperCase() || 'AI'} Intelligence...`
                                        : (draftingStage || 'Drafting Your Expert Analysis...')}
                                </h2>
                                <p className="text-gray-400 mb-8 max-w-md mx-auto">
                                    {location.state?.newsItem?.aiResearch
                                        ? `Integrating UNIQUE, multi-source real-time data from ${location.state.newsItem.provider?.toUpperCase() || 'AI Resources'} into your high-authority article.`
                                        : 'Our internal BPO logic is generating a UNIQUE, high-authority article based on industry benchmarks and EEAT principles.'}
                                </p>

                                <div className="w-full max-w-md h-2 bg-gray-800 rounded-full overflow-hidden mb-4">
                                    <div
                                        className="h-full bg-[#A2CB52] transition-all duration-300"
                                        style={{ width: `${draftProgress}%` }}
                                    ></div>
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-[#A2CB52] font-bold">
                                    {draftProgress}% Complete
                                </div>
                            </div>
                        )}
                        {error && <div className="bg-red-900/40 border border-red-500/50 text-red-200 px-4 py-3 rounded mb-6">{error}</div>}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>Title</label>
                                    <input
                                        type="text"
                                        required
                                        value={title}
                                        onChange={(e) => {
                                            setTitle(e.target.value);
                                            if (!isEditing) setSlug(slugify(e.target.value));
                                        }}
                                        className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all"
                                        style={{ background: 'var(--surface2)', borderColor: 'var(--border)', color: 'var(--text)' }}
                                        placeholder="Enter post title"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>SEO Slug</label>
                                    <input
                                        type="text"
                                        required
                                        value={slug}
                                        onChange={(e) => setSlug(slugify(e.target.value))}
                                        className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all font-mono text-sm"
                                        style={{ background: 'var(--surface2)', borderColor: 'var(--border)', color: 'var(--accent)' }}
                                        placeholder="url-friendly-slug"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>Excerpt</label>
                                <textarea
                                    rows="3"
                                    value={excerpt}
                                    onChange={(e) => setExcerpt(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all"
                                    style={{ background: 'var(--surface2)', borderColor: 'var(--border)', color: 'var(--text2)' }}
                                    placeholder="Short summary for list view and SEO"
                                />
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>Category</label>
                                    <select
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all"
                                        style={{ background: 'var(--surface2)', borderColor: 'var(--border)', color: 'var(--text)' }}
                                    >
                                        <option value="" style={{ background: 'var(--surface2)' }}>Select a Category</option>
                                        <option value="AI & Automation" style={{ background: 'var(--surface2)' }}>AI & Automation</option>
                                        <option value="Sales Strategy" style={{ background: 'var(--surface2)' }}>Sales Strategy</option>
                                        <option value="Remote Work" style={{ background: 'var(--surface2)' }}>Remote Work</option>
                                        <option value="Leadership" style={{ background: 'var(--surface2)' }}>Leadership</option>
                                        <option value="Technology" style={{ background: 'var(--surface2)' }}>Technology</option>
                                        <option value="Outsourcing" style={{ background: 'var(--surface2)' }}>Outsourcing</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>Author</label>
                                    <select
                                        value={selectedAuthor}
                                        onChange={(e) => setSelectedAuthor(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all"
                                        style={{ background: 'var(--surface2)', borderColor: 'var(--border)', color: 'var(--text)' }}
                                    >
                                        <option value="" style={{ background: 'var(--surface2)' }}>Select an Author</option>
                                        {authors.map(author => (
                                            <option key={author.id} value={author.id} style={{ background: 'var(--surface2)' }}>{author.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>Featured Image</label>
                                {(previewUrl || imageUrl) && (
                                    <div className="mb-4">
                                        <img
                                            src={previewUrl || imageUrl}
                                            alt="Preview"
                                            className="h-48 w-auto object-cover rounded-lg border"
                                            style={{ borderColor: 'var(--border)' }}
                                            onError={(e) => {
                                                e.target.src = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=1200&h=630&crop=center';
                                            }}
                                        />
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#A2CB52]/10 file:text-[#A2CB52] hover:file:bg-[#A2CB52]/20"
                                />
                            </div>

                            <div className="quill-dark-wrapper">
                                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>Content</label>
                                <style>{`
                                    .quill-dark-wrapper .ql-toolbar {
                                        background: var(--surface2) !important;
                                        border-color: var(--border) !important;
                                        border-top-left-radius: 8px;
                                        border-top-right-radius: 8px;
                                    }
                                    .quill-dark-wrapper .ql-container {
                                        background: var(--surface2) !important;
                                        border-color: var(--border) !important;
                                        border-bottom-left-radius: 8px;
                                        border-bottom-right-radius: 8px;
                                        color: var(--text) !important;
                                        font-family: 'DM Mono', monospace !important;
                                        font-size: 14px !important;
                                        min-height: 400px;
                                    }
                                    .quill-dark-wrapper .ql-stroke {
                                        stroke: var(--muted) !important;
                                    }
                                    .quill-dark-wrapper .ql-fill {
                                        fill: var(--muted) !important;
                                    }
                                    .quill-dark-wrapper .ql-picker {
                                        color: var(--muted) !important;
                                    }
                                    .quill-dark-wrapper .ql-picker-options {
                                        background: var(--surface3) !important;
                                        border-color: var(--border) !important;
                                    }
                                    .quill-dark-wrapper .ql-editor.ql-blank::before {
                                        color: var(--muted) !important;
                                        font-style: normal !important;
                                    }
                                `}</style>
                                <ReactQuill
                                    theme="snow"
                                    value={content}
                                    onChange={setContent}
                                    modules={modules}
                                    className="h-[400px] mb-12"
                                />
                            </div>

                            <div className="flex items-center space-x-3 pt-8">
                                <input
                                    type="checkbox"
                                    id="published"
                                    checked={published}
                                    onChange={(e) => setPublished(e.target.checked)}
                                    className="h-5 w-5 bg-transparent border-gray-600 rounded text-[#A2CB52] focus:ring-[#A2CB52]"
                                />
                                <label htmlFor="published" className="text-sm font-medium" style={{ color: 'var(--text2)' }}>
                                    Publish immediately
                                </label>
                            </div>

                            <div className="flex justify-end pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex items-center px-8 py-3 rounded-lg font-bold transition-all shadow-lg disabled:opacity-50 hover:scale-[1.02] active:scale-[0.98]"
                                    style={{ backgroundColor: 'var(--accent)', color: '#000' }}
                                >
                                    <Save size={20} className="mr-2" />
                                    {loading ? 'Saving...' : 'Save Post'}
                                </button>
                            </div>
                        </form>
                    </div>
                </FadeIn>
            </div>
        </div>
    );
};

export default BlogEditor;
